import { RATES, SPEED_THRESHOLD_KMH, VEHICLE_MULTIPLIERS } from "./fare";

export type Suburb = {
  slug: string;
  name: string;
  postcode: string;
  blurb: string;
  highlights: string[];
  region: "south-east" | "east" | "bayside" | "airport";
  /** Approximate road distance to Melbourne Airport (Tullamarine), in km. */
  airportKm: number;
  /** Typical off-peak drive time to Melbourne Airport, in minutes. */
  airportMin: number;
  /** Typical off-peak drive time to the Melbourne CBD, in minutes. */
  cbdMin: number;
  /** Main route used for the airport run. */
  airportRoute: string;
  /** Local landmarks and pickup points our chauffeurs know well. */
  landmarks: string[];
  /** Practical pickup guidance specific to this suburb. */
  pickupNote: string;
  /** A short, suburb-specific paragraph — local context, not boilerplate. */
  localNote: string;
  /** Neighbouring suburb slugs, used for internal linking. */
  nearby: string[];
};

export const SUBURBS: Suburb[] = [
  {
    slug: "dandenong",
    name: "Dandenong",
    postcode: "3175",
    region: "south-east",
    blurb:
      "Reliable premium cab service across Dandenong and the surrounding industrial and retail hubs. Airport transfers, corporate pickups and late-night runs, 24/7.",
    highlights: ["Melbourne Airport ~45 min", "Dandenong Station pickups", "24/7 booking"],
    airportKm: 55,
    airportMin: 45,
    cbdMin: 35,
    airportRoute: "EastLink (M3) to the Eastern Freeway, then CityLink to Tullamarine",
    landmarks: [
      "Dandenong Railway Station",
      "Dandenong Plaza",
      "Dandenong Hospital",
      "Dandenong Market",
    ],
    pickupNote:
      "For Dandenong Station we pick up on Cheltenham Road at the taxi rank; for Dandenong Plaza the Walker Street entrance is quickest at busy times.",
    localNote:
      "Dandenong's early-shift traffic on Princes Highway builds from about 6:30am, so for a flight before 9am we recommend leaving 15 minutes earlier than the map estimate. EastLink is almost always the faster airport route from here, even with the toll.",
    nearby: ["springvale", "narre-warren", "berwick", "clayton", "oakleigh"],
  },
  {
    slug: "frankston",
    name: "Frankston",
    postcode: "3199",
    region: "south-east",
    blurb:
      "Premium sedan, SUV and Maxi taxi service in Frankston and along the Nepean Highway corridor. Ideal for airport runs, corporate travel and events.",
    highlights: ["Melbourne Airport ~55 min", "Peninsula-wide pickups", "Fixed-price airport fares"],
    airportKm: 70,
    airportMin: 60,
    cbdMin: 50,
    airportRoute: "Peninsula Link to EastLink, then the Eastern Freeway and CityLink",
    landmarks: [
      "Frankston Railway Station",
      "Bayside Shopping Centre",
      "Frankston Hospital",
      "Frankston Waterfront",
    ],
    pickupNote:
      "Frankston Station pickups are on Young Street; for the hospital we meet at the main Hastings Road entrance.",
    localNote:
      "Frankston is the longest airport run we do regularly, so it is the one most worth pre-booking. Peninsula Link keeps the trip predictable outside peak, but Friday afternoons northbound can add 20 minutes.",
    nearby: ["mordialloc", "cranbourne", "dandenong", "springvale"],
  },
  {
    slug: "berwick",
    name: "Berwick",
    postcode: "3806",
    region: "south-east",
    blurb:
      "Executive cab service serving Berwick residents and businesses. Comfortable Mercedes sedans and SUVs, pre-booked or on demand.",
    highlights: ["Melbourne Airport ~50 min", "Casey Hospital transfers", "Corporate accounts"],
    airportKm: 65,
    airportMin: 55,
    cbdMin: 45,
    airportRoute: "Monash Freeway (M1) to CityLink and the Tullamarine Freeway",
    landmarks: [
      "Casey Hospital",
      "Berwick Village",
      "Federation University Berwick campus",
      "Berwick Railway Station",
    ],
    pickupNote:
      "Casey Hospital pickups are at the main entrance drop-off loop; Berwick Village pickups work best on High Street outside the shops.",
    localNote:
      "From Berwick the Monash is the direct route, but it is also the section that clogs first. For a 7am flight we usually collect at 4:45am, which keeps the whole run on free-flowing road.",
    nearby: ["narre-warren", "pakenham", "cranbourne", "dandenong"],
  },
  {
    slug: "cranbourne",
    name: "Cranbourne",
    postcode: "3977",
    region: "south-east",
    blurb:
      "Premium taxi service in Cranbourne — airport transfers, medical appointments and everyday travel in modern, spotless vehicles.",
    highlights: ["Melbourne Airport ~55 min", "Meet & greet available", "Child seats on request"],
    airportKm: 70,
    airportMin: 58,
    cbdMin: 50,
    airportRoute: "South Gippsland Highway to the Monash Freeway, then CityLink",
    landmarks: [
      "Cranbourne Park Shopping Centre",
      "Royal Botanic Gardens Cranbourne",
      "Casey Fields",
      "Cranbourne Railway Station",
    ],
    pickupNote:
      "Cranbourne Park pickups are at the Codrington Street entrance; for Casey Fields we meet at the main car park.",
    localNote:
      "Cranbourne has grown faster than its roads, so the first 15 minutes of any trip is the least predictable part. We build that into the pickup time rather than the fare.",
    nearby: ["pakenham", "berwick", "narre-warren", "frankston"],
  },
  {
    slug: "pakenham",
    name: "Pakenham",
    postcode: "3810",
    region: "south-east",
    blurb:
      "South-east Melbourne premium cab service covering Pakenham and the Cardinia corridor. Book ahead for airport, corporate or late-night trips.",
    highlights: ["Melbourne Airport ~1 hr", "Pre-booked flat fares", "SUV & Maxi available"],
    airportKm: 80,
    airportMin: 70,
    cbdMin: 60,
    airportRoute: "Princes Freeway (M1) through the Monash to CityLink",
    landmarks: [
      "Pakenham Railway Station",
      "Lakeside Pakenham",
      "Cardinia Cultural Centre",
      "Arena Shopping Centre",
    ],
    pickupNote:
      "Station pickups are on Station Street; Lakeside pickups are easiest from the Lakeside Boulevard side.",
    localNote:
      "Pakenham is the far end of our south-east coverage, so airport runs from here are always pre-booked rather than on-demand. That also means the vehicle is waiting before you are, not the other way round.",
    nearby: ["berwick", "narre-warren", "cranbourne", "dandenong"],
  },
  {
    slug: "narre-warren",
    name: "Narre Warren",
    postcode: "3805",
    region: "south-east",
    blurb:
      "Trusted premium cabs across Narre Warren, Fountain Gate and surrounds. Punctual pickups for early flights and corporate meetings.",
    highlights: ["Melbourne Airport ~45 min", "Fountain Gate pickups", "Fully insured chauffeurs"],
    airportKm: 62,
    airportMin: 52,
    cbdMin: 42,
    airportRoute: "Monash Freeway (M1) to CityLink and the Tullamarine Freeway",
    landmarks: [
      "Westfield Fountain Gate",
      "Narre Warren Railway Station",
      "Bunjil Place",
      "Casey Central",
    ],
    pickupNote:
      "At Westfield Fountain Gate we use the Overland Drive taxi rank; Bunjil Place pickups are at the Patrick Northeast Drive forecourt.",
    localNote:
      "Bunjil Place events empty out all at once, so if you are booking after a show, pre-book the return leg — rideshare surge in Narre Warren after an event is steep and the wait is long.",
    nearby: ["berwick", "dandenong", "cranbourne", "pakenham"],
  },
  {
    slug: "clayton",
    name: "Clayton",
    postcode: "3168",
    region: "south-east",
    blurb:
      "Premium chauffeured cab service in Clayton, servicing Monash University, Monash Medical Centre and the surrounding tech corridor.",
    highlights: ["Monash Uni & Hospital runs", "Airport ~40 min", "Corporate invoicing"],
    airportKm: 45,
    airportMin: 40,
    cbdMin: 30,
    airportRoute: "Monash Freeway (M1) to CityLink and the Tullamarine Freeway",
    landmarks: [
      "Monash University Clayton campus",
      "Monash Medical Centre",
      "Clayton Railway Station",
      "Australian Synchrotron",
    ],
    pickupNote:
      "Monash Medical Centre pickups are at the Clayton Road main entrance; for the university we meet at the Bus Loop on Wellington Road.",
    localNote:
      "Clayton runs on the university and hospital calendar, not the standard peak. Semester start and shift changeover around 7am and 3pm are the busiest windows for pickups here.",
    nearby: ["oakleigh", "springvale", "glen-waverley", "mount-waverley", "dandenong"],
  },
  {
    slug: "glen-waverley",
    name: "Glen Waverley",
    postcode: "3150",
    region: "east",
    blurb:
      "Executive cabs in Glen Waverley — comfortable, on time and immaculately presented. Great for business travellers and airport transfers.",
    highlights: ["Airport ~40 min", "The Glen pickups", "Executive Mercedes fleet"],
    airportKm: 45,
    airportMin: 40,
    cbdMin: 32,
    airportRoute: "Monash Freeway (M1) to CityLink, or EastLink when the Monash is heavy",
    landmarks: ["The Glen Shopping Centre", "Glen Waverley Railway Station", "Kingsway dining strip"],
    pickupNote:
      "The Glen pickups are at the Springvale Road entrance rank; Kingsway pickups are easiest on Coleman Parade.",
    localNote:
      "Kingsway is one of the busiest dining strips in the east, and late-night cabs there are genuinely hard to find on weekends. A pre-booked pickup at a set time is the difference between a 5-minute wait and a 40-minute one.",
    nearby: ["mount-waverley", "clayton", "box-hill", "blackburn", "oakleigh"],
  },
  {
    slug: "mount-waverley",
    name: "Mount Waverley",
    postcode: "3149",
    region: "east",
    blurb:
      "Local premium taxi service across Mount Waverley for airport runs, corporate travel and evening events. Quiet, spacious rides.",
    highlights: ["Airport ~35 min", "Silent-ride option", "Pre-booked pickups"],
    airportKm: 42,
    airportMin: 36,
    cbdMin: 28,
    airportRoute: "Monash Freeway (M1) to CityLink and the Tullamarine Freeway",
    landmarks: [
      "Mount Waverley Railway Station",
      "Mount Waverley Village",
      "Pinewood Shopping Centre",
    ],
    pickupNote:
      "Village pickups are on Hamilton Place; station pickups use the Stephensons Road side entrance.",
    localNote:
      "Most of our Mount Waverley work is early-morning corporate travel, so we hold the silent-ride option here by default — no radio, no conversation unless you start one.",
    nearby: ["glen-waverley", "clayton", "oakleigh", "blackburn", "box-hill"],
  },
  {
    slug: "brighton",
    name: "Brighton",
    postcode: "3186",
    region: "bayside",
    blurb:
      "Chauffeured premium sedans and SUVs across bayside Brighton. Elegant, discreet transport for events, dinners and airport transfers.",
    highlights: ["Bayside pickups", "Airport ~40 min", "Wedding & event bookings"],
    airportKm: 38,
    airportMin: 38,
    cbdMin: 25,
    airportRoute: "Nepean Highway to CityLink, then the Tullamarine Freeway",
    landmarks: [
      "Brighton Beach bathing boxes",
      "Church Street shopping strip",
      "Middle Brighton Baths",
      "Brighton Beach Station",
    ],
    pickupNote:
      "Church Street pickups work best outside the Bay Street end; for beach events we meet at the Esplanade car park.",
    localNote:
      "Brighton bookings are mostly evening and event work, where the vehicle matters as much as the trip. We send the executive sedan here unless you ask for the SUV.",
    nearby: ["bentleigh", "caulfield", "mordialloc", "malvern"],
  },
  {
    slug: "caulfield",
    name: "Caulfield",
    postcode: "3162",
    region: "south-east",
    blurb:
      "Premium taxi service around Caulfield, Caulfield Racecourse and the Monash Caulfield campus. Reliable rides, day or night.",
    highlights: ["Racecourse event transfers", "Airport ~35 min", "Student & corporate accounts"],
    airportKm: 35,
    airportMin: 35,
    cbdMin: 22,
    airportRoute: "Punt Road to CityLink, or the Monash Freeway when the city is heavy",
    landmarks: [
      "Caulfield Racecourse",
      "Monash University Caulfield campus",
      "Caulfield Station",
      "Glen Huntly Road shops",
    ],
    pickupNote:
      "On race days we meet away from the main gates on Kambrook Road — the Station Street side gridlocks.",
    localNote:
      "Caulfield Cup week changes everything about getting a cab here. We pre-position vehicles on race days and strongly recommend booking the return leg at the same time as the outbound.",
    nearby: ["malvern", "bentleigh", "oakleigh", "brighton"],
  },
  {
    slug: "malvern",
    name: "Malvern",
    postcode: "3144",
    region: "south-east",
    blurb:
      "Executive cab service in Malvern for residents and businesses along the Glenferrie corridor. Immaculate vehicles, professional chauffeurs.",
    highlights: ["Airport ~35 min", "CBD in ~20 min", "Corporate accounts welcome"],
    airportKm: 32,
    airportMin: 33,
    cbdMin: 20,
    airportRoute: "CityLink via Punt Road or Toorak Road",
    landmarks: [
      "Glenferrie Road shops",
      "Malvern Central",
      "Cabrini Hospital",
      "Malvern Railway Station",
    ],
    pickupNote:
      "Cabrini Hospital pickups are at the Wattletree Road entrance; Malvern Central is easiest from the Station Street side.",
    localNote:
      "Cabrini discharge pickups are a regular run for us, and they rarely happen on schedule. We hold the vehicle rather than re-dispatch it, so you are not starting the booking again from the ward.",
    nearby: ["caulfield", "camberwell", "hawthorn", "brighton"],
  },
  {
    slug: "oakleigh",
    name: "Oakleigh",
    postcode: "3166",
    region: "south-east",
    blurb:
      "Premium cabs across Oakleigh and the Eaton Mall precinct. Fixed-price airport transfers and comfortable local rides in modern Mercedes vehicles.",
    highlights: ["Airport ~35 min", "Eaton Mall pickups", "SUV & Maxi options"],
    airportKm: 40,
    airportMin: 36,
    cbdMin: 27,
    airportRoute: "Monash Freeway (M1) to CityLink and the Tullamarine Freeway",
    landmarks: ["Eaton Mall", "Oakleigh Railway Station", "Oakleigh Central", "Chadstone nearby"],
    pickupNote:
      "Eaton Mall is a pedestrian strip, so pickups are on Chester Street or Atherton Road at the corner.",
    localNote:
      "Eaton Mall stays busy long after the rest of Oakleigh has closed, and the strip itself is closed to traffic. Knowing to wait on Chester Street saves most passengers a confused ten minutes.",
    nearby: ["clayton", "caulfield", "glen-waverley", "springvale", "mount-waverley"],
  },
  {
    slug: "springvale",
    name: "Springvale",
    postcode: "3171",
    region: "south-east",
    blurb:
      "24/7 premium taxi service in Springvale — airport transfers, hospital appointments and everyday travel in spotless, chauffeured vehicles.",
    highlights: ["Airport ~40 min", "24/7 bookings", "Multi-lingual chauffeurs"],
    airportKm: 50,
    airportMin: 42,
    cbdMin: 35,
    airportRoute: "EastLink (M3) to the Eastern Freeway, then CityLink",
    landmarks: [
      "Springvale Railway Station",
      "Springvale Road shopping strip",
      "Sandown Racecourse",
      "Springvale Homemaker Centre",
    ],
    pickupNote:
      "Springvale Station pickups are on Balmoral Avenue; the shopping strip is easiest from Buckingham Avenue.",
    localNote:
      "Springvale Road through the shopping strip is slow at almost any hour, so we route around it via Police Road for airport runs. Several of our chauffeurs speak Vietnamese and Mandarin, which matters for family and elderly pickups here.",
    nearby: ["dandenong", "clayton", "oakleigh", "mordialloc"],
  },
  {
    slug: "mordialloc",
    name: "Mordialloc",
    postcode: "3195",
    region: "bayside",
    blurb:
      "Bayside premium cab service covering Mordialloc, Aspendale and Parkdale. Perfect for early flights, restaurant nights and event pickups.",
    highlights: ["Airport ~45 min", "Bayside dining runs", "Pre-booked flat fares"],
    airportKm: 50,
    airportMin: 45,
    cbdMin: 38,
    airportRoute: "Mornington Peninsula Freeway to EastLink, then the Eastern Freeway and CityLink",
    landmarks: [
      "Main Street Mordialloc",
      "Mordialloc Pier",
      "Mordialloc Railway Station",
      "Braeside Park",
    ],
    pickupNote:
      "Main Street pickups are outside the Albert Street corner; pier events use the Beach Road car park.",
    localNote:
      "Summer weekends on Beach Road are the slow point here, not the freeway. If you are heading to the airport on a hot Saturday we go inland from the start rather than following the coast.",
    nearby: ["frankston", "bentleigh", "springvale", "brighton"],
  },
  {
    slug: "bentleigh",
    name: "Bentleigh",
    postcode: "3204",
    region: "bayside",
    blurb:
      "Reliable premium cabs across Bentleigh and Bentleigh East. Punctual airport transfers, corporate pickups and local rides, 24/7.",
    highlights: ["Airport ~35 min", "CBD in ~25 min", "Corporate accounts"],
    airportKm: 38,
    airportMin: 36,
    cbdMin: 25,
    airportRoute: "Nepean Highway to CityLink and the Tullamarine Freeway",
    landmarks: ["Centre Road shops", "Bentleigh Railway Station", "GESAC Bentleigh East"],
    pickupNote:
      "Centre Road pickups are best just off the Jasper Road corner, away from the tram-stop congestion.",
    localNote:
      "Bentleigh's grid of side streets is easy to get wrong at 5am in the dark. We confirm the exact house number by text before the driver leaves, so nobody is circling the block.",
    nearby: ["brighton", "caulfield", "mordialloc", "oakleigh"],
  },
  {
    slug: "box-hill",
    name: "Box Hill",
    postcode: "3128",
    region: "east",
    blurb:
      "Premium chauffeured cabs in Box Hill — airport transfers, hospital runs and corporate pickups across Melbourne's eastern hub.",
    highlights: ["Airport ~35 min", "Box Hill Hospital transfers", "24/7 bookings"],
    airportKm: 38,
    airportMin: 35,
    cbdMin: 28,
    airportRoute: "Eastern Freeway (M3) to CityLink and the Tullamarine Freeway",
    landmarks: [
      "Box Hill Hospital",
      "Box Hill Central",
      "Box Hill Institute",
      "Box Hill Transport Interchange",
    ],
    pickupNote:
      "Hospital pickups are at the Nelson Road entrance; Box Hill Central is easiest from the Market Street side rather than the bus interchange.",
    localNote:
      "Box Hill has one of the best airport runs in the east — the Eastern Freeway to CityLink is a straight shot, and outside peak we regularly do it in 35 minutes door to terminal.",
    nearby: ["blackburn", "doncaster", "camberwell", "mitcham", "glen-waverley"],
  },
  {
    slug: "doncaster",
    name: "Doncaster",
    postcode: "3108",
    region: "east",
    blurb:
      "Premium taxi service in Doncaster — executive cabs, fixed-price Melbourne Airport transfers and 24/7 local rides across the Westfield Doncaster precinct.",
    highlights: ["Airport ~30 min", "Westfield Doncaster pickups", "Pre-booked flat fares"],
    airportKm: 33,
    airportMin: 30,
    cbdMin: 25,
    airportRoute: "Eastern Freeway (M3) straight onto CityLink and the Tullamarine Freeway",
    landmarks: [
      "Westfield Doncaster",
      "Doncaster Park & Ride",
      "Ruffey Lake Park",
      "Doncaster Hill",
    ],
    pickupNote:
      "Westfield Doncaster pickups are at the Williamsons Road taxi rank on the ground level, not the rooftop car park.",
    localNote:
      "Doncaster is one of the quickest airport runs on this side of the city — the Eastern Freeway feeds directly onto CityLink with no suburban crawl at either end. Outside peak, 30 minutes from Doncaster Hill to the terminal is realistic, which is why so many early-flight passengers here book the night before rather than gambling on a morning rideshare.",
    nearby: ["box-hill", "balwyn", "blackburn", "kew", "mitcham"],
  },
  {
    slug: "ringwood",
    name: "Ringwood",
    postcode: "3134",
    region: "east",
    blurb:
      "Reliable premium taxis in Ringwood and Eastland — for airport transfers, corporate travel and everyday rides in modern vehicles.",
    highlights: ["Airport ~40 min", "Eastland pickups", "SUV & Maxi available"],
    airportKm: 45,
    airportMin: 40,
    cbdMin: 35,
    airportRoute: "EastLink to the Eastern Freeway, then CityLink",
    landmarks: ["Eastland Shopping Centre", "Ringwood Station", "Ringwood Lake", "Karralyka Centre"],
    pickupNote:
      "Eastland pickups are at the Ringwood Street rank; the station interchange is a poor pickup point at peak.",
    localNote:
      "Ringwood sits right at the EastLink junction, which makes airport timing unusually reliable from here — you are on freeway within a couple of minutes of leaving home.",
    nearby: ["croydon", "mitcham", "blackburn", "box-hill"],
  },
  {
    slug: "blackburn",
    name: "Blackburn",
    postcode: "3130",
    region: "east",
    blurb:
      "Premium cabs in Blackburn and along the eastern rail corridor. Punctual, professional chauffeurs for airport and corporate travel.",
    highlights: ["Airport ~35 min", "Station pickups", "Corporate accounts"],
    airportKm: 40,
    airportMin: 36,
    cbdMin: 30,
    airportRoute: "Eastern Freeway (M3) to CityLink and the Tullamarine Freeway",
    landmarks: ["Blackburn Station", "Blackburn Lake Sanctuary", "Forest Hill Chase nearby"],
    pickupNote:
      "Blackburn Station pickups are on South Parade; the level crossing side can be blocked at peak.",
    localNote:
      "Blackburn's side streets around the lake are narrow and poorly lit at night. Our drivers wait at the nearest through-road corner and walk to the door if that is easier for you.",
    nearby: ["box-hill", "mitcham", "doncaster", "glen-waverley", "ringwood"],
  },
  {
    slug: "camberwell",
    name: "Camberwell",
    postcode: "3124",
    region: "east",
    blurb:
      "Executive chauffeured cabs across Camberwell Junction and surrounds. Elegant Mercedes vehicles for dinners, events and airport runs.",
    highlights: ["Airport ~30 min", "CBD in ~20 min", "Event & wedding bookings"],
    airportKm: 32,
    airportMin: 30,
    cbdMin: 20,
    airportRoute: "Eastern Freeway (M3) to CityLink and the Tullamarine Freeway",
    landmarks: [
      "Camberwell Junction",
      "Camberwell Market",
      "Rivoli Cinemas",
      "Camberwell Station",
    ],
    pickupNote:
      "Junction pickups are on Prospect Hill Road rather than Burke Road, which is slow in both directions.",
    localNote:
      "Sunday morning is Camberwell Market, and Burke Road becomes a genuine bottleneck. We route via Riversdale Road for airport runs before 1pm on Sundays.",
    nearby: ["hawthorn", "kew", "balwyn", "box-hill", "malvern"],
  },
  {
    slug: "hawthorn",
    name: "Hawthorn",
    postcode: "3122",
    region: "east",
    blurb:
      "Premium taxi service in Hawthorn — quick access to the CBD, Swinburne University and Melbourne Airport in comfort and style.",
    highlights: ["Airport ~30 min", "Swinburne Uni pickups", "24/7 bookings"],
    airportKm: 28,
    airportMin: 28,
    cbdMin: 15,
    airportRoute: "Eastern Freeway (M3) to CityLink and the Tullamarine Freeway",
    landmarks: [
      "Swinburne University",
      "Glenferrie Road",
      "Hawthorn Station",
      "Auburn Village",
    ],
    pickupNote:
      "Glenferrie Road pickups are best on the side streets — Burwood Road or Liddiard Street — because of the tram stops.",
    localNote:
      "Hawthorn is close enough to the city that the CBD leg is often quicker than the walk to the tram, and the airport run via the Eastern Freeway is one of the shortest we do from the east.",
    nearby: ["kew", "camberwell", "malvern", "balwyn"],
  },
  {
    slug: "kew",
    name: "Kew",
    postcode: "3101",
    region: "east",
    blurb:
      "Chauffeured premium cabs across Kew and Studley Park. Discreet, immaculate vehicles for airport, corporate and social travel.",
    highlights: ["Airport ~25 min", "CBD in ~15 min", "Executive Mercedes fleet"],
    airportKm: 26,
    airportMin: 26,
    cbdMin: 15,
    airportRoute: "Eastern Freeway (M3) directly onto CityLink",
    landmarks: ["Studley Park", "High Street Kew", "Kew Junction", "Xavier College"],
    pickupNote:
      "Kew Junction pickups are on Cotham Road; the Studley Park Road side backs up badly at peak.",
    localNote:
      "Kew is the closest of our eastern suburbs to the airport by road time — the Eastern Freeway entrance is minutes away and there is no suburban section at the other end.",
    nearby: ["hawthorn", "balwyn", "camberwell", "doncaster"],
  },
  {
    slug: "balwyn",
    name: "Balwyn",
    postcode: "3103",
    region: "east",
    blurb:
      "Executive cab service across Balwyn and Balwyn North. Reliable pickups for early flights, meetings and evening events.",
    highlights: ["Airport ~30 min", "Pre-booked flat fares", "Corporate accounts"],
    airportKm: 30,
    airportMin: 30,
    cbdMin: 22,
    airportRoute: "Eastern Freeway (M3) via Burke Road, then CityLink",
    landmarks: ["Whitehorse Road shops", "Balwyn Cinema", "Maranoa Gardens", "Balwyn High School"],
    pickupNote:
      "Whitehorse Road pickups are easiest in the side streets off Balwyn Road at school drop-off times.",
    localNote:
      "School traffic around Balwyn High between 8:15am and 9am adds more delay than the freeway ever does. For a mid-morning flight we either leave before 8am or take Doncaster Road instead.",
    nearby: ["kew", "camberwell", "doncaster", "box-hill", "hawthorn"],
  },
  {
    slug: "mitcham",
    name: "Mitcham",
    postcode: "3132",
    region: "east",
    blurb:
      "Premium chauffeured cabs in Mitcham and Nunawading. On-time airport transfers and corporate rides in modern, spotless vehicles.",
    highlights: ["Airport ~40 min", "Eastern Freeway access", "SUV & Maxi options"],
    airportKm: 45,
    airportMin: 38,
    cbdMin: 32,
    airportRoute: "Eastern Freeway (M3) to CityLink and the Tullamarine Freeway",
    landmarks: ["Mitcham Station", "Brickworks Park", "Nunawading Station", "Eastland nearby"],
    pickupNote:
      "Mitcham Station pickups are at the Whitehorse Road end; Nunawading is easiest from Springvale Road.",
    localNote:
      "Mitcham gets the full benefit of the Eastern Freeway once you are on it, but Springvale Road to get there is the slow part. We usually go via Mitcham Road instead in the morning.",
    nearby: ["blackburn", "ringwood", "box-hill", "doncaster", "croydon"],
  },
  {
    slug: "croydon",
    name: "Croydon",
    postcode: "3136",
    region: "east",
    blurb:
      "Reliable premium taxi service in Croydon and the outer east. Comfortable rides for airport, corporate and local travel.",
    highlights: ["Airport ~45 min", "24/7 bookings", "Baby seats on request"],
    airportKm: 52,
    airportMin: 45,
    cbdMin: 42,
    airportRoute: "Maroondah Highway to EastLink, then the Eastern Freeway and CityLink",
    landmarks: ["Croydon Station", "Arndale Shopping Centre", "Croydon Main Street", "Dorset Road"],
    pickupNote:
      "Main Street pickups work best outside the Civic Square end; Arndale pickups use the Kent Avenue car park.",
    localNote:
      "Croydon is the outer edge of our regular eastern coverage, so on-demand pickups here can take longer than in Ringwood. Booking the night before removes that entirely.",
    nearby: ["ringwood", "mitcham", "blackburn"],
  },
  {
    slug: "melbourne-airport",
    name: "Melbourne Airport",
    postcode: "3045",
    region: "airport",
    blurb:
      "Premium chauffeured cab service to and from Melbourne Airport (Tullamarine, MEL) — 24/7 airport transfers with flight tracking and meet-and-greet.",
    highlights: ["All 4 terminals (T1–T4)", "Flight tracking included", "Meet & greet available"],
    airportKm: 0,
    airportMin: 0,
    cbdMin: 25,
    airportRoute: "Tullamarine Freeway (M2) to CityLink",
    landmarks: ["Terminal 1 (Qantas)", "Terminal 2 (International)", "Terminal 3 (Virgin)", "Terminal 4 (Jetstar)"],
    pickupNote:
      "Arrivals pickups are at the terminal taxi rank unless you book meet-and-greet, in which case your chauffeur waits inside at the arrivals gate with a name board.",
    localNote:
      "We track your flight number, so a delayed landing does not mean a missed car or a re-booking. The 30 minutes of free waiting time after touchdown covers baggage on all but the busiest international arrivals.",
    nearby: ["tullamarine", "gladstone-park", "westmeadows", "airport-west", "essendon"],
  },
  {
    slug: "tullamarine",
    name: "Tullamarine",
    postcode: "3043",
    region: "airport",
    blurb:
      "Local premium cabs in Tullamarine — minutes from Melbourne Airport. Ideal for airline crew, hotels and quick airport transfers.",
    highlights: ["Airport ~7 min", "Hotel & crew transfers", "24/7 bookings"],
    airportKm: 5,
    airportMin: 7,
    cbdMin: 25,
    airportRoute: "Melrose Drive directly to the terminal precinct",
    landmarks: ["Melrose Drive hotels", "Tullamarine Village", "Airport freight precinct"],
    pickupNote:
      "Hotel pickups are at the lobby entrance; crew transfers are timed to the roster, not the flight.",
    localNote:
      "Tullamarine trips are short but time-critical — a 7-minute run only works if the car is already there. We arrive early on these rather than on the dot.",
    nearby: ["melbourne-airport", "gladstone-park", "westmeadows", "attwood"],
  },
  {
    slug: "essendon",
    name: "Essendon",
    postcode: "3040",
    region: "airport",
    blurb:
      "Premium taxi service in Essendon and Essendon Fields. Fast, comfortable rides to Melbourne Airport and the CBD.",
    highlights: ["Airport ~15 min", "CBD in ~15 min", "Executive Mercedes fleet"],
    airportKm: 12,
    airportMin: 15,
    cbdMin: 20,
    airportRoute: "Tullamarine Freeway (M2) north",
    landmarks: [
      "Essendon Fields Airport",
      "Essendon Station",
      "Keilor Road shops",
      "DFO Essendon",
    ],
    pickupNote:
      "Essendon Fields pickups are at the terminal forecourt; Keilor Road pickups are easiest on the side streets.",
    localNote:
      "Essendon serves two airports — Essendon Fields for charter and Tullamarine for commercial. We confirm which one on booking, because the two are a 15-minute drive apart.",
    nearby: ["airport-west", "keilor", "tullamarine", "melbourne-airport"],
  },
  {
    slug: "keilor",
    name: "Keilor",
    postcode: "3036",
    region: "airport",
    blurb:
      "Reliable premium cabs in Keilor and Keilor East — a short hop from Melbourne Airport with fixed-price transfers available.",
    highlights: ["Airport ~14 min", "Fixed-price transfers", "SUV & Maxi options"],
    airportKm: 12,
    airportMin: 14,
    cbdMin: 25,
    airportRoute: "Calder Freeway to the Tullamarine Freeway",
    landmarks: ["Keilor Village", "Brimbank Park", "Overnewton Castle", "Keilor East shops"],
    pickupNote:
      "Keilor Village pickups are on Old Calder Highway; Brimbank Park uses the main car park entrance.",
    localNote:
      "Keilor is close enough that passengers often leave too late — the drive is short, but the Calder merge at peak can double it. We still recommend a 90-minute buffer for domestic flights.",
    nearby: ["airport-west", "essendon", "gladstone-park", "tullamarine"],
  },
  {
    slug: "airport-west",
    name: "Airport West",
    postcode: "3042",
    region: "airport",
    blurb:
      "Premium chauffeured cabs in Airport West — minutes from Melbourne Airport. Perfect for early flights and hotel pickups.",
    highlights: ["Airport ~10 min", "24/7 bookings", "Meet & greet available"],
    airportKm: 8,
    airportMin: 10,
    cbdMin: 22,
    airportRoute: "Matthews Avenue to the Tullamarine Freeway",
    landmarks: ["Westfield Airport West", "Matthews Avenue", "Essendon Fields nearby"],
    pickupNote:
      "Westfield Airport West pickups are at the Louis Street entrance rank.",
    localNote:
      "Airport West is our most common late-notice airport booking, simply because it is close enough to still make the flight. Even so, the last pickup we can promise is 75 minutes before a domestic departure.",
    nearby: ["essendon", "keilor", "gladstone-park", "tullamarine"],
  },
  {
    slug: "gladstone-park",
    name: "Gladstone Park",
    postcode: "3043",
    region: "airport",
    blurb:
      "Local premium taxi service in Gladstone Park — direct access to Melbourne Airport and the Tullamarine Freeway.",
    highlights: ["Airport ~7 min", "Pre-booked flat fares", "Corporate accounts"],
    airportKm: 5,
    airportMin: 7,
    cbdMin: 27,
    airportRoute: "South Centre Road to Melrose Drive and the terminals",
    landmarks: ["Gladstone Park Shopping Centre", "Gladstone Park Reserve", "Melrose Drive"],
    pickupNote:
      "Shopping centre pickups are at the Gladstone Park Drive entrance.",
    localNote:
      "Gladstone Park sits under the flight path and right beside the freeway, which makes it one of the fastest terminal runs in Melbourne — but also one where traffic on Melrose Drive at shift change is the only real variable.",
    nearby: ["tullamarine", "westmeadows", "attwood", "melbourne-airport"],
  },
  {
    slug: "westmeadows",
    name: "Westmeadows",
    postcode: "3049",
    region: "airport",
    blurb:
      "Premium chauffeured cabs in Westmeadows — a stone's throw from Melbourne Airport terminals.",
    highlights: ["Airport ~8 min", "Hotel transfers", "24/7 bookings"],
    airportKm: 5,
    airportMin: 8,
    cbdMin: 28,
    airportRoute: "Mickleham Road to the Tullamarine Freeway",
    landmarks: ["Westmeadows Village", "Broadmeadows Valley Park", "Mickleham Road"],
    pickupNote:
      "Village pickups are on Fawkner Street outside the shops.",
    localNote:
      "Westmeadows is one of the few places where a pre-dawn airport run does not mean a pre-dawn wake-up. Most of our bookings here are collected 70 minutes before departure and still arrive comfortably early.",
    nearby: ["gladstone-park", "attwood", "tullamarine", "melbourne-airport"],
  },
  {
    slug: "attwood",
    name: "Attwood",
    postcode: "3049",
    region: "airport",
    blurb:
      "Executive cab service in Attwood — quick, comfortable transfers to Melbourne Airport and the CBD.",
    highlights: ["Airport ~8 min", "CBD in ~28 min", "SUV & Maxi options"],
    airportKm: 6,
    airportMin: 8,
    cbdMin: 28,
    airportRoute: "Mickleham Road to the Tullamarine Freeway",
    landmarks: ["Attwood Reserve", "Mickleham Road", "Gladstone Park nearby"],
    pickupNote:
      "Residential pickups only — we confirm the street number by text before the driver departs.",
    localNote:
      "Attwood is almost entirely residential, so nearly every booking here is a home-to-terminal airport run. Group bookings are common, which is why we keep a Maxi available on this side of the airport.",
    nearby: ["westmeadows", "gladstone-park", "tullamarine", "melbourne-airport"],
  },
];

export const SUBURB_MAP: Record<string, Suburb> = Object.fromEntries(
  SUBURBS.map((s) => [s.slug, s]),
);

/**
 * Indicative day-rate airport fare for a suburb, using the published Victorian
 * metered rates. Deterministic (no `new Date()`) so server and client render
 * identical markup.
 */
export function indicativeAirportFare(
  suburb: Suburb,
  vehicle: keyof typeof VEHICLE_MULTIPLIERS | string = "Premium Sedan",
): { low: number; high: number } | null {
  if (suburb.airportKm <= 0) return null;
  const rate = RATES.day;
  const fastMinutes = (suburb.airportKm / SPEED_THRESHOLD_KMH) * 60;
  const slowMinutes = Math.max(0, suburb.airportMin - fastMinutes);
  const base =
    (rate.flagfall + suburb.airportKm * rate.perKm + slowMinutes * rate.perMin) *
    (VEHICLE_MULTIPLIERS[vehicle] ?? 1);
  return { low: Math.round((base * 0.9) / 5) * 5, high: Math.round((base * 1.15) / 5) * 5 };
}

/** Suburb-specific FAQ entries, built from the suburb's own data. */
export function suburbFaq(s: Suburb): { q: string; a: string }[] {
  const fare = indicativeAirportFare(s);
  const faq: { q: string; a: string }[] = [];

  if (s.airportKm > 0) {
    faq.push({
      q: `How long does a taxi take from ${s.name} to Melbourne Airport?`,
      a: `About ${s.airportMin} minutes outside peak hour, covering roughly ${s.airportKm} km via ${s.airportRoute}. Allow extra time on weekday mornings between 7am and 9am.`,
    });
  }
  if (fare) {
    faq.push({
      q: `How much is a cab from ${s.name} to Melbourne Airport?`,
      a: `An indicative day-rate fare in a Premium Sedan is around $${fare.low}–$${fare.high}, based on the published Victorian metered rates. Overnight and peak rates are higher, and we can confirm a fixed price when you book.`,
    });
  }
  faq.push({
    q: `Where do you pick up in ${s.name}?`,
    a: `${s.pickupNote} We also do door-to-door residential pickups anywhere in ${s.name} (${s.postcode}).`,
  });
  faq.push({
    q: `Can I book a Maxi taxi in ${s.name}?`,
    a: `Yes. We run Premium Sedans, Premium SUVs (up to 6 passengers) and Maxi taxis (up to 11 passengers) across ${s.name}. Maxi vehicles are best pre-booked, especially for airport runs with luggage.`,
  });
  faq.push({
    q: `Do you operate in ${s.name} 24/7?`,
    a: `Yes — we take bookings and run pickups in ${s.name} around the clock, including pre-dawn airport transfers and late-night returns. Call 0493 100 265 or book online.`,
  });
  return faq;
}
